import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, map, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoadingService } from '../shared/loading.service';

import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AccountInfo,
  AuthenticationResult,
  EventType,
  InteractionStatus
} from '@azure/msal-browser';
import { NotificationService } from '../notification/notification.service';

@Injectable({
  providedIn: 'root'
})
export class AdminAuthService implements OnDestroy {
  private apiUrlAdminAuth = `${environment.baseApimUrl}/admin/auth`;
  private _msalService = inject(MsalService);
  private _msalBroadcastService = inject(MsalBroadcastService);
  private _router = inject(Router);
  private _destroyRef = inject(DestroyRef);
  private readonly _destroying$ = new Subject<void>();

  public account$ = new BehaviorSubject<AccountInfo | null>(null);
  public isAuthenticated$ = this.account$.pipe(map((account) => !!account));

  private _accessToken: string | null = null;

  getAccessToken(): string | null {
    return this._accessToken;
  }

  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private notificationService: NotificationService
  ) {
    // this._listenMsal();
    this._initializeMsal();
  }

  private async _initializeMsal(): Promise<void> {
    try {
      await this._msalService.instance.initialize();

      const accounts = this._msalService.instance.getAllAccounts();
      if (accounts && accounts.length > 0) {
        this._msalService.instance.setActiveAccount(accounts[0]);
        this.account$.next(accounts[0]);
        try {
          const silentResult = await this._msalService.instance.acquireTokenSilent({
            scopes: ['user.read', 'openid', 'profile', 'email'],
            account: accounts[0]
          });
          this._accessToken = silentResult.accessToken;
        } catch (e) {
          console.warn('Silent token acquisition failed on init', e);
        }
      }

      this._initMsalListeners();
    } catch (err) {
      console.error('MSAL initialization error', err);
      this.notificationService.error(String(err));
      this._initMsalListeners();
    }
  }

  private _initMsalListeners(): void {
    this._msalBroadcastService.msalSubject$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((msg) => {
        console.log('Msal event: ', msg);
        if (
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ) {
          const authResult = msg.payload as AuthenticationResult;
          console.log('msg.payload / authresult: ', authResult);

          if (authResult?.account) {
            this._msalService.instance.setActiveAccount(authResult.account);
            this.account$.next(authResult.account);
          }
          if (authResult?.accessToken) {
            this._accessToken = authResult.accessToken;
          }

          if (msg.eventType === EventType.LOGIN_SUCCESS) {
            this._router.navigate(['/admin/pagina-inicial']);
          }
          return;
        }

        if (
          msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
          msg.eventType === EventType.SSO_SILENT_FAILURE
        ) {
          this._accessToken = null;
          this.account$.next(null);
          this._router.navigate(['/admin/login']);
        }
      });
  }

  async login(): Promise<AuthenticationResult | null> {
    try {
      const loginResult = await this._msalService.instance.loginPopup({
        scopes: ['user.read', 'openid', 'profile', 'email'],
        prompt: 'select_account'
      });

      console.log('Token: ', loginResult.accessToken);
      console.log('Claims: ', loginResult.idTokenClaims);
      console.log('Full Result: ', loginResult);
      return loginResult;
    } catch (error) {
      console.error('Login error: ', error);
      this.notificationService.error(error as string);
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      await this._msalService.instance.logoutPopup();
    } catch (error) {
      console.error('Logout error: ', error);
      this.notificationService.error('Erro ao fazer logout');
    } finally {
      this._accessToken = null;
      this.account$.next(null);
      await this._router.navigate(['/admin/login']);
    }
  }

  ngOnDestroy(): void {
    this.account$.complete();
  }

  private _listenMsal(): void {
    this._msalBroadcastService.inProgress$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (status) => {
          const acquireTokenOrLogin =
            status === InteractionStatus.AcquireToken ||
            status === InteractionStatus.Login;
          if (acquireTokenOrLogin) this._router.navigateByUrl('').then();
        }
      });
  }
}
